import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Gem, MapPin, Star } from 'lucide-react';
import { popIn } from '@/shared/motion/motionPresets';
import type { NavigatorProvider } from './navigator-results.types';
import { avatarColorClass, getInitials } from './avatar';
import { DeliveryModeChips } from './DeliveryModeChips';
import { NavigatorReasonCallout } from './NavigatorReasonCallout';

type NavigatorProviderCardProps = {
  provider: NavigatorProvider;
  /** Selection is still the AI top match — amber chip (the default state). */
  isTopMatch?: boolean;
  /** User swapped away from the top match — sage selection chip instead. */
  isSelection?: boolean;
  /** Featured-slot hero treatment: sage ring + soft wash behind the header. */
  featured?: boolean;
  /** Feature 087 (ticket 15) — provider is the plan's global Best Match. */
  isBestMatch?: boolean;
  bestMatchLabel?: string;
  topMatchLabel?: string;
  selectionLabel?: string;
  whyThisMatchLabel?: string;
  actions?: ReactNode;
};

/**
 * Featured provider card shared by the care-seeker results, guest results, and
 * shared-care-guide views. Mirrors the FP results page's MatchedProviderCard
 * (initials avatar, role line, location + rating, AI reason callout, 2-line
 * bio, chip) so every audience sees the same card; role-specific buttons are
 * injected through the `actions` slot.
 */
export const NavigatorProviderCard = ({
  provider,
  isTopMatch = false,
  isSelection = false,
  featured = false,
  isBestMatch = false,
  bestMatchLabel = 'Best Match',
  topMatchLabel = 'Top Match',
  selectionLabel = 'Your Selection',
  whyThisMatchLabel = 'Why this match',
  actions,
}: NavigatorProviderCardProps) => {
  const displayName = provider.name?.trim() || 'Provider';
  const role = provider.categoryLabel || provider.specialtyName;
  const initials = getInitials(displayName);
  const avatarColor = avatarColorClass(displayName);
  const rating =
    typeof provider.rating === 'number' && provider.rating > 0 ? provider.rating.toFixed(1) : null;
  const description = provider.bio?.trim();
  const aiReason = provider.aiReason?.trim();

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border bg-card-base p-6 transition-shadow duration-150 ease-out motion-reduce:transition-none hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)] ${
        featured
          ? 'border-sage/30 ring-1 ring-sage/25'
          : 'border-border-subtle hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]'
      }`}
    >
      {featured && (
        // Faint sage wash behind the avatar/name band, fading out by mid-card —
        // differentiates the plan's provider without a heavy color block.
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-sage-bg to-transparent"
          aria-hidden
        />
      )}

      {/* Corner badge stack: Best Match (when present) sits above Top Match/
          Selection instead of a separate banner row — a richer gold foil +
          gem icon reads as the elevated, cross-specialty tier without a new
          brand color (feature 087, ticket 15; approved treatment from
          ticket 10's prototype). */}
      <div className="absolute right-5 top-5 flex flex-col items-end gap-1.5">
        {isBestMatch && (
          <motion.span
            variants={popIn}
            initial="initial"
            animate="animate"
            title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
            aria-label={`${bestMatchLabel}: Highest-scoring provider across this care plan. Primary specialty breaks score ties.`}
            className="inline-flex items-center gap-1 rounded-md border border-[hsl(38_55%_68%)]/60 bg-gradient-to-br from-[hsl(43_75%_94%)] via-[hsl(38_78%_88%)] to-[hsl(30_68%_78%)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[hsl(30_65%_26%)] shadow-[0_2px_8px_rgba(160,120,30,0.25)]"
          >
            <Gem className="h-3 w-3" aria-hidden />
            {bestMatchLabel}
          </motion.span>
        )}
        {isTopMatch && (
          <span
            title="Highest-ranked provider for this specialty."
            aria-label={`${topMatchLabel}: Highest-ranked provider for this specialty.`}
            className="rounded-md bg-[hsl(40_75%_88%)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-[hsl(35_55%_28%)]"
          >
            {topMatchLabel}
          </span>
        )}
        {!isTopMatch && isSelection && (
          <span className="rounded-md bg-sage px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.06em] text-white">
            {selectionLabel}
          </span>
        )}
      </div>

      <div className="relative flex items-start gap-5">
        <div
          className={`flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-base font-semibold text-white ${avatarColor}`}
          aria-hidden
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1 pr-24">
          <h3 className="font-termina text-lg font-medium text-text-primary">{displayName}</h3>
          {role && <p className="text-sm text-text-secondary">{role}</p>}

          {(provider.location || rating || provider.specialtyDeliveryModes?.length) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary">
              {provider.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" aria-hidden />
                  {provider.location}
                </span>
              )}
              {rating && (
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-sage text-sage" aria-hidden />
                  <span className="text-text-primary">{rating}</span>
                </span>
              )}
              <DeliveryModeChips modes={provider.specialtyDeliveryModes} />
            </div>
          )}

          {aiReason && <NavigatorReasonCallout reason={aiReason} label={whyThisMatchLabel} />}

          {description && (
            <p className="mt-3 line-clamp-2 text-sm text-text-secondary">{description}</p>
          )}

          {actions && <div className="mt-5 flex flex-wrap items-center gap-3">{actions}</div>}
        </div>
      </div>
    </article>
  );
};
